import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  User,
  CreditCard,
  Home,
  UserCircle,
  Clock,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

interface InvitationDetails {
  id: string;
  guestName: string | null;
  guestDni: string | null;
  category: string;
  unit: {
    unitNumber: string;
    blockSection: string | null;
  };
  hostName: string | null;
  validFrom: string | Date;
  validUntil: string | Date | null;
  suggestedDirection: string;
  lastAccessTime: string | Date | null;
  lastAccessDirection: string | null;
}

interface ScanResultProps {
  valid: boolean;
  reason?: string;
  invitation: InvitationDetails | null;
  onLogAccess: (direction: "INBOUND" | "OUTBOUND") => void;
  onReset: () => void;
  isLogging: boolean;
}

export function ScanResult({
  valid,
  reason,
  invitation,
  onLogAccess,
  onReset,
  isLogging,
}: ScanResultProps) {
  if (!valid) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            Código QR inválido
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{reason}</p>
          <Button onClick={onReset} variant="outline" className="w-full">
            Escanear de nuevo
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!invitation) {
    return null;
  }

  const unitDisplay = invitation.unit.blockSection
    ? `${invitation.unit.blockSection} - ${invitation.unit.unitNumber}`
    : invitation.unit.unitNumber;

  const categoryLabels: Record<string, string> = {
    TEMPORARY: "Temporal",
    PERMANENT: "Permanente",
    SERVICE: "Proveedor",
  };

  return (
    <Card className="border-green-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
          Invitación válida
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{invitation.guestName}</span>
            <Badge variant="secondary">
              {categoryLabels[invitation.category] || invitation.category}
            </Badge>
          </div>

          {invitation.guestDni && (
            <div className="flex items-center gap-3">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span>{invitation.guestDni}</span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Home className="h-4 w-4 text-muted-foreground" />
            <span>{unitDisplay}</span>
          </div>

          <div className="flex items-center gap-3">
            <UserCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Anfitrión: {invitation.hostName}
            </span>
          </div>

          {invitation.lastAccessTime && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                Último:{" "}
                {invitation.lastAccessDirection === "INBOUND"
                  ? "Ingresó"
                  : "Egresó"}{" "}
                a las {format(new Date(invitation.lastAccessTime), "HH:mm")}
              </span>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <p className="text-sm text-muted-foreground mb-3">
            Registrar acceso:
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => onLogAccess("INBOUND")}
              disabled={isLogging}
              variant={
                invitation.suggestedDirection === "INBOUND"
                  ? "default"
                  : "outline"
              }
              className="flex-1"
            >
              {isLogging ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowDownToLine className="mr-2 h-4 w-4" />
              )}
              Ingreso
            </Button>
            <Button
              onClick={() => onLogAccess("OUTBOUND")}
              disabled={isLogging}
              variant={
                invitation.suggestedDirection === "OUTBOUND"
                  ? "default"
                  : "outline"
              }
              className="flex-1"
            >
              {isLogging ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowUpFromLine className="mr-2 h-4 w-4" />
              )}
              Egreso
            </Button>
          </div>
        </div>

        <Button onClick={onReset} variant="ghost" className="w-full">
          Cancelar
        </Button>
      </CardContent>
    </Card>
  );
}
